import type { User } from "@/schema"
import type { UserCapability } from "@/schema/user-capabilities"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"
import { useDevAuth as useAuth } from "@/hooks/use-dev-auth"

import { updateCurrentUser, updateUserCapabilities } from "./api"

export function useUpdateCurrentUser() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  const { userId, orgId } = useAuth()

  return useMutation({
    mutationFn: async (user: Partial<User>) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateCurrentUser(context, user)
    },
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(["user", "me", userId, orgId], updatedUser)
      queryClient.invalidateQueries({ queryKey: ["user", "me"] })
    },
  })
}

export function useUpdateUserCapabilities() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      userId,
      capabilities,
      expected_capabilities,
    }: {
      userId: string
      capabilities: UserCapability[]
      expected_capabilities: UserCapability[]
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateUserCapabilities(context, userId, capabilities, expected_capabilities)
    },
    onSuccess: (updatedUser) => {
      const changedCurrentUser = queryClient
        .getQueriesData<User>({ queryKey: ["user", "me"] })
        .some(([, currentUser]) => currentUser?.id === updatedUser.id)
      if (changedCurrentUser) {
        // Permission changes can affect every cached resource.
        queryClient.invalidateQueries()
      } else {
        queryClient.invalidateQueries({ queryKey: ["company-users"] })
      }
    },
  })
}
